import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Search, Loader2, FileText, Plus, Trash2, RotateCcw, Pencil } from "lucide-react";
import aapi, { inr, fmtDate, formatApiError } from "./aapi";
import { DateTimeFields } from "./DateTimeFields";
import { PageHeader, inputCls, selectCls } from "@/panel/components";
import { EASE } from "@/components/Motion";

const ORDER_STATUSES = ["pending", "in_progress", "completed", "cancelled", "partial", "refunded"];
const EMPTY_ORDER = { order_id: "", service_name: "", quantity: "", charge: "", status: "completed", created_at: "" };
const EMPTY_TXN = { type: "credit", amount: "", method: "", note: "", created_at: "" };
const toIso = (v) => (v ? new Date(v).toISOString() : new Date().toISOString());
const sortByDate = (arr) => [...arr].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
const isoToLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function AdminStatements() {
  const [q, setQ] = useState("");
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sel, setSel] = useState(null);
  const [orders, setOrders] = useState([]);
  const [txns, setTxns] = useState([]);
  const [past, setPast] = useState([]);
  const [incOrders, setIncOrders] = useState(true);
  const [incTxns, setIncTxns] = useState(true);
  const [emailCustomer, setEmailCustomer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [newOrder, setNewOrder] = useState({ ...EMPTY_ORDER });
  const [newTxn, setNewTxn] = useState({ ...EMPTY_TXN });
  const [openingBalance, setOpeningBalance] = useState("0");
  const [edit, setEdit] = useState(null); // { kind: "order"|"txn", i, draft }

  const startEdit = (kind, i, row) => setEdit({ kind, i, draft: { ...row, created_at: isoToLocal(row.created_at) } });
  const cancelEdit = () => setEdit(null);
  const saveEdit = () => {
    if (!edit) return;
    const { kind, i, draft } = edit;
    if (kind === "order") {
      const row = {
        ...orders[i],
        order_id: (draft.order_id || "").trim() || orders[i].order_id,
        service_name: (draft.service_name || "").trim() || "Custom entry",
        quantity: parseInt(draft.quantity, 10) || 0,
        charge: parseFloat(draft.charge) || 0,
        status: draft.status,
        created_at: toIso(draft.created_at),
      };
      setOrders((o) => sortByDate(o.map((r, j) => (j === i ? row : r))));
    } else {
      const row = {
        ...txns[i],
        type: draft.type === "credit" ? "credit" : "debit",
        amount: parseFloat(draft.amount) || 0,
        method: (draft.method || "").trim(),
        note: (draft.note || "").trim(),
        created_at: toIso(draft.created_at),
      };
      setTxns((t) => sortByDate(t.map((r, j) => (j === i ? row : r))));
    }
    setEdit(null);
    toast.success("Row updated — auto re-sequenced");
  };

  const search = async () => {
    if (q.trim().length < 3) return toast.error("Kam se kam 3 characters likhein");
    setSearching(true);
    try {
      const { data } = await aapi.get(`/users?search=${encodeURIComponent(q.trim())}`);
      setMatches(data);
      if (!data.length) toast.error("Koi user nahi mila");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSearching(false);
    }
  };

  const pick = async (u) => {
    setResult(null);
    try {
      const { data } = await aapi.get(`/users/${u.id}/statement-data`);
      setSel(data.user);
      setOrders(sortByDate(data.orders));
      setTxns(sortByDate(data.transactions));
      setPast(data.past_statements);
      setOpeningBalance("0");
      setMatches([]);
      setQ("");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addOrder = () => {
    if (!newOrder.service_name.trim()) return toast.error("Service name required");
    setOrders((o) => sortByDate([{
      order_id: newOrder.order_id.trim() || `BK-${100000 + Math.floor(Math.random() * 899999)}`,
      service_name: newOrder.service_name.trim(),
      quantity: parseInt(newOrder.quantity, 10) || 0,
      charge: parseFloat(newOrder.charge) || 0,
      status: newOrder.status,
      created_at: toIso(newOrder.created_at),
      link: "",
    }, ...o]));
    setNewOrder({ ...EMPTY_ORDER });
  };

  const addTxn = () => {
    if (!(parseFloat(newTxn.amount) > 0)) return toast.error("Amount required");
    setTxns((t) => sortByDate([{
      txn_id: `TXN-${Math.random().toString(16).slice(2, 8).toUpperCase()}`,
      type: newTxn.type,
      amount: parseFloat(newTxn.amount),
      method: newTxn.method.trim(),
      note: newTxn.note.trim(),
      created_at: toIso(newTxn.created_at),
    }, ...t]));
    setNewTxn({ ...EMPTY_TXN });
  };

  const generate = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await aapi.post(`/users/${sel.id}/statement/custom`, {
        orders, txns,
        include_orders: incOrders, include_transactions: incTxns,
        email_customer: emailCustomer,
        opening_balance: parseFloat(openingBalance) || 0,
      });
      setResult(data);
      toast.success(`Statement ${data.statement_id} generated${data.emailed ? " and emailed" : ""}`);
      const { data: fresh } = await aapi.get(`/users/${sel.id}/statement-data`);
      setPast(fresh.past_statements);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const rowCls = "grid grid-cols-12 items-center gap-2 border-b border-white/5 px-4 py-2.5 last:border-0";
  const headCls = "grid grid-cols-12 gap-2 border-b border-white/10 bg-white/[0.02] px-4 py-2.5 font-mono text-[9px] uppercase tracking-[0.18em] text-neutral-500";

  return (
    <div data-testid="admin-statements-page">
      <PageHeader title="Statement Editor" sub="Kisi bhi customer ka statement dekho, orders/transactions add ya remove karo, phir custom QR-verified PDF banao." />

      {/* user picker */}
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
        className="max-w-2xl rounded-2xl border border-white/10 bg-panel/80 p-6">
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Customer</label>
        <div className="mt-2 flex gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-600" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="Email ya name se search karein" className={`${inputCls} pl-10`} data-testid="stmt-user-search" />
          </div>
          <button onClick={search} disabled={searching} data-testid="stmt-user-search-btn"
            className="rounded-xl border border-gold/40 px-5 text-xs font-bold text-goldlight transition-colors hover:bg-gold/10 disabled:opacity-40">
            {searching ? <Loader2 size={14} className="animate-spin" /> : "SEARCH"}
          </button>
        </div>
        {matches.length > 0 && (
          <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-white/10" data-testid="stmt-user-matches">
            {matches.map((u) => (
              <button key={u.id} onClick={() => pick(u)} data-testid={`stmt-user-option-${u.id}`}
                className="flex w-full items-center justify-between border-b border-white/5 px-4 py-2.5 text-left text-xs last:border-0 hover:bg-gold/[0.06]">
                <span className="text-neutral-200">{u.email}</span>
                <span className="font-mono text-[10px] text-neutral-500">{u.name || "—"} · {u.orders_count} orders</span>
              </button>
            ))}
          </div>
        )}
        {sel && (
          <div className="mt-4 flex items-center justify-between rounded-xl border border-gold/25 bg-gold/[0.05] px-4 py-3" data-testid="stmt-selected-user">
            <div>
              <div className="text-sm font-semibold text-neutral-100">{sel.name || "Customer"}</div>
              <div className="font-mono text-[10px] text-neutral-500">{sel.email} · balance {inr(sel.balance)}</div>
            </div>
            <button onClick={() => pick(sel)} data-testid="stmt-reset-data"
              className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-[10px] font-semibold text-neutral-400 hover:border-gold/40 hover:text-goldlight">
              <RotateCcw size={11} /> RESET TO LIVE DATA
            </button>
          </div>
        )}
      </motion.div>

      {sel && (
        <>
          {/* ORDERS editor */}
          <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.05 }}
            className="mt-6 rounded-2xl border border-white/10 bg-panel/80 p-6" data-testid="stmt-orders-section">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-neutral-100">Orders in statement <span className="ml-2 font-mono text-[10px] text-neutral-500">{orders.length}</span></h3>
              <label className="flex cursor-pointer items-center gap-2">
                <input type="checkbox" checked={incOrders} onChange={(e) => setIncOrders(e.target.checked)}
                  className="h-4 w-4 accent-gold" data-testid="stmt-include-orders" />
                <span className="text-[11px] text-neutral-400">Show section in PDF</span>
              </label>
            </div>
            <div className="mt-4 overflow-x-auto rounded-xl border border-white/10">
              <div className="min-w-[640px]">
                <div className={headCls}>
                  <span className="col-span-2">Order ID</span><span className="col-span-4">Service</span>
                  <span className="col-span-1">Qty</span><span className="col-span-2">Charge</span>
                  <span className="col-span-2">Status</span><span className="col-span-1 text-right">Remove</span>
                </div>
                {orders.map((o, i) => (
                  <div key={`${o.order_id}-${i}`} data-testid={`stmt-order-row-${o.order_id}`}>
                    <div className={rowCls}>
                      <span className="col-span-2 font-mono text-[10px] text-gold/80">{o.order_id}</span>
                      <span className="col-span-4 truncate text-xs text-neutral-300" title={o.service_name}>{o.service_name}</span>
                      <span className="col-span-1 text-[11px] text-neutral-400">{(o.quantity || 0).toLocaleString("en-IN")}</span>
                      <span className="col-span-2 text-xs text-neutral-200">{inr(o.charge)}</span>
                      <span className="col-span-2 font-mono text-[9px] uppercase text-neutral-500">{String(o.status).replace("_", " ")}</span>
                      <span className="col-span-1 flex items-center justify-end gap-2">
                        <button onClick={() => startEdit("order", i, o)} data-testid={`stmt-order-edit-${i}`}
                          aria-label="Edit order" className="text-neutral-600 transition-colors hover:text-gold">
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => setOrders((x) => x.filter((_, j) => j !== i))} data-testid={`stmt-order-remove-${i}`}
                          aria-label="Remove order" className="text-neutral-600 transition-colors hover:text-rose-400">
                          <Trash2 size={13} />
                        </button>
                      </span>
                    </div>
                    {edit?.kind === "order" && edit.i === i && (
                      <div className="border-b border-white/5 bg-gold/[0.04] px-4 py-3" data-testid="stmt-order-edit-panel">
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                          <input value={edit.draft.order_id} onChange={(e) => setEdit({ ...edit, draft: { ...edit.draft, order_id: e.target.value } })}
                            placeholder="Order ID" className={`${inputCls} py-2 font-mono text-xs`} data-testid="stmt-edit-order-id" />
                          <input value={edit.draft.service_name} onChange={(e) => setEdit({ ...edit, draft: { ...edit.draft, service_name: e.target.value } })}
                            placeholder="Service name" className={`${inputCls} py-2 text-xs`} data-testid="stmt-edit-order-service" />
                          <select value={edit.draft.status} onChange={(e) => setEdit({ ...edit, draft: { ...edit.draft, status: e.target.value } })}
                            className={`${selectCls} py-2 text-xs`} data-testid="stmt-edit-order-status">
                            {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                          </select>
                          <input value={edit.draft.quantity} onChange={(e) => setEdit({ ...edit, draft: { ...edit.draft, quantity: e.target.value.replace(/\D/g, "") } })}
                            placeholder="Quantity" inputMode="numeric" className={`${inputCls} py-2 text-xs`} data-testid="stmt-edit-order-qty" />
                          <input value={edit.draft.charge} onChange={(e) => setEdit({ ...edit, draft: { ...edit.draft, charge: e.target.value.replace(/[^\d.]/g, "") } })}
                            placeholder="Charge ₹" inputMode="decimal" className={`${inputCls} py-2 text-xs`} data-testid="stmt-edit-order-charge" />
                          <input type="datetime-local" value={edit.draft.created_at}
                            onChange={(e) => setEdit({ ...edit, draft: { ...edit.draft, created_at: e.target.value } })}
                            className={`${inputCls} py-2 text-xs`} data-testid="stmt-edit-order-date" />
                        </div>
                        <div className="mt-3 flex gap-2">
                          <button onClick={saveEdit} data-testid="stmt-order-edit-save"
                            className="rounded-lg bg-gradient-to-br from-goldlight via-gold to-golddark px-4 py-1.5 text-[10px] font-bold text-black">
                            SAVE ROW
                          </button>
                          <button onClick={cancelEdit} data-testid="stmt-order-edit-cancel"
                            className="rounded-lg border border-white/10 px-4 py-1.5 text-[10px] font-semibold text-neutral-400 hover:border-white/25">
                            CANCEL
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {orders.length === 0 && <p className="px-4 py-4 text-center text-[11px] text-neutral-600">No orders — neeche se add karein</p>}
              </div>
            </div>
            {/* add order */}
            <div className="mt-4 rounded-xl border border-dashed border-gold/25 p-4" data-testid="stmt-add-order">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <input value={newOrder.service_name} onChange={(e) => setNewOrder({ ...newOrder, service_name: e.target.value })}
                  placeholder="Service name *" className={`${inputCls} col-span-2`} data-testid="stmt-order-service" />
                <input value={newOrder.order_id} onChange={(e) => setNewOrder({ ...newOrder, order_id: e.target.value })}
                  placeholder="Order ID (auto)" className={`${inputCls} font-mono`} data-testid="stmt-order-id" />
                <select value={newOrder.status} onChange={(e) => setNewOrder({ ...newOrder, status: e.target.value })}
                  className={selectCls} data-testid="stmt-order-status">
                  {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
                <input value={newOrder.quantity} onChange={(e) => setNewOrder({ ...newOrder, quantity: e.target.value.replace(/\D/g, "") })}
                  placeholder="Quantity" inputMode="numeric" className={inputCls} data-testid="stmt-order-qty" />
                <input value={newOrder.charge} onChange={(e) => setNewOrder({ ...newOrder, charge: e.target.value.replace(/[^\d.]/g, "") })}
                  placeholder="Charge ₹" inputMode="decimal" className={inputCls} data-testid="stmt-order-charge" />
                <div className="col-span-2">
                  <DateTimeFields value={newOrder.created_at} onChange={(v) => setNewOrder({ ...newOrder, created_at: v })} testid="stmt-order-date" allowEmpty />
                </div>
              </div>
              <button onClick={addOrder} data-testid="stmt-add-order-btn"
                className="mt-3 flex items-center gap-2 rounded-lg border border-gold/40 px-4 py-2 text-[11px] font-bold text-goldlight transition-colors hover:bg-gold/10">
                <Plus size={13} /> ADD ORDER ROW
              </button>
            </div>
          </motion.section>

          {/* TRANSACTIONS editor */}
          <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
            className="mt-6 rounded-2xl border border-white/10 bg-panel/80 p-6" data-testid="stmt-txns-section">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-neutral-100">Transactions in statement <span className="ml-2 font-mono text-[10px] text-neutral-500">{txns.length}</span></h3>
              <label className="flex cursor-pointer items-center gap-2">
                <input type="checkbox" checked={incTxns} onChange={(e) => setIncTxns(e.target.checked)}
                  className="h-4 w-4 accent-gold" data-testid="stmt-include-txns" />
                <span className="text-[11px] text-neutral-400">Show section in PDF</span>
              </label>
            </div>
            <div className="mt-4 overflow-x-auto rounded-xl border border-white/10">
              <div className="min-w-[640px]">
                <div className={headCls}>
                  <span className="col-span-2">TXN ID</span><span className="col-span-2">Type</span>
                  <span className="col-span-2">Amount</span><span className="col-span-4">Note / Method</span>
                  <span className="col-span-2 text-right">Remove</span>
                </div>
                {txns.map((t, i) => (
                  <div key={`${t.txn_id}-${i}`} data-testid={`stmt-txn-row-${t.txn_id}`}>
                    <div className={rowCls}>
                      <span className="col-span-2 font-mono text-[10px] text-gold/80">{t.txn_id}</span>
                      <span className="col-span-2">
                        <span className={`rounded-full px-2 py-0.5 font-mono text-[9px] ${t.type === "credit" ? "bg-emerald-400/15 text-emerald-300" : "bg-rose-400/15 text-rose-300"}`}>
                          {String(t.type).toUpperCase()}
                        </span>
                      </span>
                      <span className="col-span-2 text-xs text-neutral-200">{inr(t.amount)}</span>
                      <span className="col-span-4 truncate text-[11px] text-neutral-400" title={t.note || t.method}>{t.note || t.method || "—"}</span>
                      <span className="col-span-2 flex items-center justify-end gap-2">
                        <button onClick={() => startEdit("txn", i, t)} data-testid={`stmt-txn-edit-${i}`}
                          aria-label="Edit transaction" className="text-neutral-600 transition-colors hover:text-gold">
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => setTxns((x) => x.filter((_, j) => j !== i))} data-testid={`stmt-txn-remove-${i}`}
                          aria-label="Remove transaction" className="text-neutral-600 transition-colors hover:text-rose-400">
                          <Trash2 size={13} />
                        </button>
                      </span>
                    </div>
                    {edit?.kind === "txn" && edit.i === i && (
                      <div className="border-b border-white/5 bg-gold/[0.04] px-4 py-3" data-testid="stmt-txn-edit-panel">
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                          <select value={edit.draft.type} onChange={(e) => setEdit({ ...edit, draft: { ...edit.draft, type: e.target.value } })}
                            className={`${selectCls} py-2 text-xs`} data-testid="stmt-edit-txn-type">
                            <option value="credit">Credit (+)</option>
                            <option value="debit">Debit (−)</option>
                          </select>
                          <input value={edit.draft.amount} onChange={(e) => setEdit({ ...edit, draft: { ...edit.draft, amount: e.target.value.replace(/[^\d.]/g, "") } })}
                            placeholder="Amount ₹" inputMode="decimal" className={`${inputCls} py-2 text-xs`} data-testid="stmt-edit-txn-amount" />
                          <input value={edit.draft.method || ""} onChange={(e) => setEdit({ ...edit, draft: { ...edit.draft, method: e.target.value } })}
                            placeholder="Method" className={`${inputCls} py-2 text-xs`} data-testid="stmt-edit-txn-method" />
                          <input value={edit.draft.note || ""} onChange={(e) => setEdit({ ...edit, draft: { ...edit.draft, note: e.target.value } })}
                            placeholder="Note / description" className={`${inputCls} py-2 text-xs`} data-testid="stmt-edit-txn-note" />
                          <input type="datetime-local" value={edit.draft.created_at}
                            onChange={(e) => setEdit({ ...edit, draft: { ...edit.draft, created_at: e.target.value } })}
                            className={`${inputCls} py-2 text-xs`} data-testid="stmt-edit-txn-date" />
                        </div>
                        <div className="mt-3 flex gap-2">
                          <button onClick={saveEdit} data-testid="stmt-txn-edit-save"
                            className="rounded-lg bg-gradient-to-br from-goldlight via-gold to-golddark px-4 py-1.5 text-[10px] font-bold text-black">
                            SAVE ROW
                          </button>
                          <button onClick={cancelEdit} data-testid="stmt-txn-edit-cancel"
                            className="rounded-lg border border-white/10 px-4 py-1.5 text-[10px] font-semibold text-neutral-400 hover:border-white/25">
                            CANCEL
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {txns.length === 0 && <p className="px-4 py-4 text-center text-[11px] text-neutral-600">No transactions — neeche se add karein</p>}
              </div>
            </div>
            {/* add txn */}
            <div className="mt-4 rounded-xl border border-dashed border-gold/25 p-4" data-testid="stmt-add-txn">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <select value={newTxn.type} onChange={(e) => setNewTxn({ ...newTxn, type: e.target.value })}
                  className={selectCls} data-testid="stmt-txn-type">
                  <option value="credit">Credit (+)</option>
                  <option value="debit">Debit (−)</option>
                </select>
                <input value={newTxn.amount} onChange={(e) => setNewTxn({ ...newTxn, amount: e.target.value.replace(/[^\d.]/g, "") })}
                  placeholder="Amount ₹ *" inputMode="decimal" className={inputCls} data-testid="stmt-txn-amount" />
                <input value={newTxn.method} onChange={(e) => setNewTxn({ ...newTxn, method: e.target.value })}
                  placeholder="Method (UPI/Bank…)" className={inputCls} data-testid="stmt-txn-method" />
                <input value={newTxn.note} onChange={(e) => setNewTxn({ ...newTxn, note: e.target.value })}
                  placeholder="Note / description" className={inputCls} data-testid="stmt-txn-note" />
                <div className="col-span-2">
                  <DateTimeFields value={newTxn.created_at} onChange={(v) => setNewTxn({ ...newTxn, created_at: v })} testid="stmt-txn-date" allowEmpty />
                </div>
              </div>
              <p className="mt-2 font-mono text-[9px] text-neutral-600">Running balance har row ka date-sequence mein auto-calculate hoga — manually dalne ki zaroorat nahi.</p>
              <button onClick={addTxn} data-testid="stmt-add-txn-btn"
                className="mt-3 flex items-center gap-2 rounded-lg border border-gold/40 px-4 py-2 text-[11px] font-bold text-goldlight transition-colors hover:bg-gold/10">
                <Plus size={13} /> ADD TRANSACTION ROW
              </button>
            </div>
          </motion.section>

          {/* generate */}
          <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
            className="mt-6 rounded-2xl border border-gold/20 bg-panel/80 p-6">
            {/* live auto-calculated summary */}
            <div className="rounded-xl border border-gold/25 bg-gold/[0.05] p-4" data-testid="stmt-live-summary">
              <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-gold/70">AUTO-CALCULATED — PDF mein yahi jayega</div>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div>
                  <div className="font-mono text-[8px] uppercase tracking-[0.15em] text-neutral-500">Total orders</div>
                  <div className="mt-0.5 text-sm font-bold text-neutral-100" data-testid="stmt-sum-orders">
                    {orders.length} <span className="text-[9px] font-normal text-neutral-500">({orders.filter((o) => o.status === "completed").length} done · {orders.filter((o) => ["pending", "in_progress", "partial"].includes(o.status)).length} active · {orders.filter((o) => ["cancelled", "refunded"].includes(o.status)).length} cancelled)</span>
                  </div>
                </div>
                <div>
                  <div className="font-mono text-[8px] uppercase tracking-[0.15em] text-neutral-500">Total credits</div>
                  <div className="mt-0.5 text-sm font-bold text-emerald-300" data-testid="stmt-sum-credits">{inr(txns.filter((t) => t.type === "credit").reduce((s, t) => s + (parseFloat(t.amount) || 0), 0))}</div>
                </div>
                <div>
                  <div className="font-mono text-[8px] uppercase tracking-[0.15em] text-neutral-500">Total debits</div>
                  <div className="mt-0.5 text-sm font-bold text-rose-300" data-testid="stmt-sum-debits">{inr(txns.filter((t) => t.type !== "credit").reduce((s, t) => s + (parseFloat(t.amount) || 0), 0))}</div>
                </div>
                <div>
                  <div className="font-mono text-[8px] uppercase tracking-[0.15em] text-neutral-500">Closing balance</div>
                  <div className="mt-0.5 text-sm font-bold text-goldlight" data-testid="stmt-sum-closing">
                    {inr((parseFloat(openingBalance) || 0) + txns.reduce((s, t) => s + (t.type === "credit" ? 1 : -1) * (parseFloat(t.amount) || 0), 0))}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-end gap-3 border-t border-white/5 pt-3">
                <div className="w-44">
                  <label className="font-mono text-[8px] uppercase tracking-[0.15em] text-neutral-500">Opening balance (₹)</label>
                  <input value={openingBalance} onChange={(e) => setOpeningBalance(e.target.value.replace(/[^\d.-]/g, ""))}
                    inputMode="decimal" className={`${inputCls} mt-1 py-2 font-mono text-xs`} data-testid="stmt-opening-balance" />
                </div>
                <p className="pb-2 text-[10px] leading-relaxed text-neutral-600">
                  Rows date-sequence mein auto-arrange hote hain; closing = opening + credits − debits.
                </p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-4">
              <label className="flex cursor-pointer items-center gap-2.5">
                <input type="checkbox" checked={emailCustomer} onChange={(e) => setEmailCustomer(e.target.checked)}
                  className="h-4 w-4 accent-gold" data-testid="stmt-email-toggle" />
                <span className="text-xs text-neutral-300">Email PDF to customer</span>
              </label>
              <button onClick={generate} disabled={busy || (orders.length === 0 && txns.length === 0)} data-testid="stmt-generate-btn"
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-sm font-bold tracking-[0.12em] text-black disabled:opacity-40">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
                GENERATE CUSTOM STATEMENT{emailCustomer ? " + EMAIL" : ""}
              </button>
            </div>

            {result && (
              <div className="mt-4 rounded-xl border border-gold/30 bg-gold/[0.06] px-4 py-4" data-testid="stmt-result">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs font-bold text-goldlight" data-testid="stmt-doc-id">{result.statement_id}</span>
                  <span data-testid="stmt-status"
                    className={`rounded-full px-2.5 py-1 font-mono text-[9px] tracking-[0.12em] ${result.emailed ? "bg-emerald-400/15 text-emerald-300" : "bg-amber-400/15 text-amber-300"}`}>
                    {result.emailed ? "EMAILED" : "SAVED — NOT EMAILED"}
                  </span>
                </div>
                <div className="mt-3 flex gap-2">
                  <a href={result.file_url} target="_blank" rel="noreferrer" data-testid="stmt-download-link"
                    className="flex-1 rounded-lg bg-gradient-to-br from-goldlight via-gold to-golddark py-2 text-center text-[11px] font-bold tracking-wide text-black transition-opacity hover:opacity-90">
                    DOWNLOAD PDF
                  </a>
                  <button type="button" data-testid="stmt-copy-link"
                    onClick={() => { navigator.clipboard.writeText(result.verify_url); toast.success("Verify link copied"); }}
                    className="flex-1 rounded-lg border border-gold/40 py-2 text-[11px] font-bold tracking-wide text-goldlight transition-colors hover:bg-gold/10">
                    COPY VERIFY LINK
                  </button>
                </div>
              </div>
            )}
          </motion.section>

          {/* past statements */}
          {past.length > 0 && (
            <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.2 }}
              className="mt-6 rounded-2xl border border-white/10 bg-panel/80 p-6" data-testid="stmt-past-list">
              <h3 className="text-sm font-semibold text-neutral-100">Previously generated — {sel.email}</h3>
              <div className="mt-4 space-y-2">
                {past.map((s) => (
                  <div key={s.statement_id} className="flex items-center justify-between rounded-xl border border-white/5 px-4 py-3"
                    data-testid={`stmt-past-${s.statement_id}`}>
                    <div>
                      <span className="font-mono text-xs text-gold/80">{s.statement_id}</span>
                      <span className="ml-3 rounded-full bg-white/5 px-2 py-0.5 font-mono text-[9px] uppercase text-neutral-500">{s.kind}</span>
                      <span className="ml-3 text-[10px] text-neutral-500">{s.orders_count} orders · {s.txns_count} txns · {fmtDate(s.generated_at)}</span>
                    </div>
                    {s.has_file ? (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/statements/file/${s.statement_id}`} target="_blank" rel="noreferrer"
                        data-testid={`stmt-past-download-${s.statement_id}`}
                        className="rounded-lg border border-gold/40 px-3 py-1.5 text-[10px] font-bold text-goldlight transition-colors hover:bg-gold/10">
                        VIEW / DOWNLOAD
                      </a>
                    ) : (
                      <span className="font-mono text-[9px] text-neutral-600">NO FILE</span>
                    )}
                  </div>
                ))}
              </div>
            </motion.section>
          )}
        </>
      )}
    </div>
  );
}
